import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Color from 'color';
import values from 'lodash/values';
import { Motion } from 'react-motion';
import { springify, springable } from '../../lib/Motion';
import { EMPTY_OBJECT, MODAL_LAYOUT, RESPONSIVE_SIZE } from '../../constants';
import Hoverable from '../Hoverable';
import { X } from '../Icons';
import Aspect from '../Aspect';
import Section from '../Section';
import Style from '../Style';
const FULL_STYLE = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

class Modal extends React.PureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    layout: PropTypes.oneOf(values(MODAL_LAYOUT)).isRequired,
    styles: PropTypes.object,
    colors: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    backgroundContent: PropTypes.node,
    fullContent: PropTypes.node,
    leftContent: PropTypes.node,
    rightContent: PropTypes.node,
  };
  static defaultProps = {
    styles: EMPTY_OBJECT,
  };

  constructor(props) {
    super(props);

    const defaultStyles = this.getStyles(props);
    this.state = {
      fullContentBaseStyle: springable(defaultStyles.fullContentStyle),
      leftBaseStyle: springable(defaultStyles.leftStyle),
      rightBaseStyle: springable(defaultStyles.rightStyle),
    };
  }

  closeClicked = e => {
    this.props.onClose();
  };

  getStyles = props => {
    let containerMargin = '1rem auto 20rem';
    let closeButtonBottomMargin = '-1rem';
    let closeButtonHeight = 0;
    let closeButtonMaxWidth = '2rem';
    let closeButtonPosition = 'relative';
    let leftMax = 0;
    let rightMax = 100;
    let scrollBoxPadding = '1rem';
    let borderRadius = '0.6rem';
    let maxHeight = undefined;

    if (props.responsive >= RESPONSIVE_SIZE.PHONE) {
      containerMargin = '5rem auto 10rem';
      closeButtonBottomMargin = undefined;
      closeButtonHeight = undefined;
      closeButtonMaxWidth = undefined;
      closeButtonPosition = 'static';
      scrollBoxPadding = '5rem';
    }
    if (props.responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT) {
      leftMax = 20;
    }
    if (props.responsive >= RESPONSIVE_SIZE.TABLET_LANDSCAPE) {
      containerMargin = '10rem auto 10rem';
      maxHeight = '60rem';
      leftMax = 100;
    }

    const composedStyles = {
      backgroundStyle: {
        backgroundColor: Style.Colors.black,
        minHeight: '60rem',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
      },
      scrollBoxStyle: {
        padding: scrollBoxPadding,
        paddingRight: `calc(${scrollBoxPadding} + 5rem)`,
        marginRight: '-5rem',
        overflowY: 'scroll',
        maxHeight: '100vh',
      },
      closeButtonStyle: {
        position: closeButtonPosition,
        top: '2rem',
        left: '2rem',
        marginBottom: closeButtonBottomMargin,
        zIndex: 500,
        maxWidth: closeButtonMaxWidth,
        maxHeight: closeButtonHeight,
        display: 'block',
        cursor: 'pointer',
      },
      containerStyle: {
        position: 'relative',
        zIndex: 10,
        boxShadow: `2.4rem 2.4rem 0 0 ${props.colors.highlight}`,
        borderRadius: borderRadius,
        overflow: 'hidden',
        maxWidth: '105rem',
        margin: containerMargin,
      },
      fullContentStyle: {
        ...FULL_STYLE,
        zIndex: 1,
        opacity: 0,
        pointerEvents: 'none',
        color: Style.Colors.white,
      },
      splitterStyle: {
        position: 'relative',
        zIndex: 5,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        width: '100%',
      },
      leftStyle: {
        position: 'relative',
        overflow: 'hidden',
        opacity: 0,
        maxHeight: maxHeight,
        maxWidth: leftMax,
        pointerEvents: 'none',
        color: Style.Colors.white,
      },
      rightStyle: {
        overflow: 'hidden',
        flexBasis: 0,
        width: 0,
        maxWidth: rightMax,
        maxHeight: maxHeight,
        backgroundColor: Style.Colors.white,
      },
    };

    switch (props.layout) {
      case MODAL_LAYOUT.BACKGROUND:
        break;
      case MODAL_LAYOUT.FULL:
        composedStyles.fullContentStyle.opacity = 1;
        composedStyles.fullContentStyle.pointerEvents = undefined;
        composedStyles.splitterStyle.pointerEvents = 'none';
        composedStyles.leftStyle.maxWidth = 0;
        composedStyles.rightStyle.maxWidth = 0;
        break;
      case MODAL_LAYOUT.EQUAL_SPLIT:
        composedStyles.leftStyle.opacity = 1;
        composedStyles.leftStyle.pointerEvents = undefined;
        composedStyles.leftStyle.flexBasis = 50;
        composedStyles.leftStyle.width = 50;
        composedStyles.rightStyle.flexBasis = 50;
        composedStyles.rightStyle.width = 50;
        break;
      case MODAL_LAYOUT.LEFT:
        composedStyles.leftStyle.opacity = 1;
        composedStyles.leftStyle.pointerEvents = undefined;
        composedStyles.leftStyle.flexBasis = 80;
        composedStyles.leftStyle.width = 80;
        composedStyles.rightStyle.flexBasis = 20;
        composedStyles.rightStyle.width = 20;
        break;
      case MODAL_LAYOUT.RIGHT:
        composedStyles.leftStyle.opacity = 1;
        composedStyles.leftStyle.pointerEvents = undefined;
        composedStyles.leftStyle.flexBasis = 20;
        composedStyles.leftStyle.width = 20;
        composedStyles.rightStyle.flexBasis = 80;
        composedStyles.rightStyle.width = 80;
        break;
      default:
    }

    Object.keys(composedStyles).map(
      styleKey =>
        (composedStyles[styleKey] = {
          ...composedStyles[styleKey],
          ...(props.styles[styleKey] || EMPTY_OBJECT),
        }),
    );

    return composedStyles;
  };

  render() {
    const {
      layout,
      styles,
      colors,
      onClose,
      backgroundContent,
      fullContent,
      leftContent,
      rightContent,
      children,
      ...otherProps
    } = this.props;

    const composedStyles = this.getStyles(this.props);

    return (
      <div
        style={{
          overflowX: 'hidden',
          backgroundColor: Color(colors.light).alpha(0.98),
          position: 'fixed',
          zIndex: 3000,
          minHeight: '100vh',
          minWidth: '100vw',
          top: 0,
          left: 0,
        }}
      >
        <Section
          style={{
            position: 'absolute',
            top: '30rem',
            width: '100vw',
            zIndex: 1,
          }}
          angle={20}
          topMargin={0}
          backgroundColor={colors.dark}
        />
        <div style={composedStyles.scrollBoxStyle}>
          <Hoverable
            style={composedStyles.closeButtonStyle}
            onClick={this.closeClicked}
          >
            <X
              style={{ maxWidth: composedStyles.closeButtonStyle.maxWidth }}
              color={colors.dark}
            />
          </Hoverable>
          <div style={composedStyles.containerStyle}>
            <div style={composedStyles.backgroundStyle}>
              <div style={{ ...FULL_STYLE, pointerEvents: 'none' }}>
                {backgroundContent}
              </div>
              <div style={{ ...FULL_STYLE, zIndex: 1 }}>
                <Motion
                  defaultStyle={this.state.fullContentBaseStyle}
                  style={springify(composedStyles.fullContentStyle)}
                >
                  {tweens => (
                    <div
                      style={{ ...composedStyles.fullContentStyle, ...tweens }}
                    >
                      {fullContent}
                    </div>
                  )}
                </Motion>
              </div>

              <div style={composedStyles.splitterStyle}>
                <Motion
                  defaultStyle={this.state.leftBaseStyle}
                  style={springify(composedStyles.leftStyle)}
                >
                  {tweens => (
                    <div
                      style={{
                        ...composedStyles.leftStyle,
                        ...tweens,
                        opacity: tweens.opacity,
                        flex: `1 1 ${tweens.flexBasis}%`,
                        width: `${tweens.width}%`,
                        maxWidth: `${tweens.maxWidth}%`,
                      }}
                    >
                      {leftContent}
                    </div>
                  )}
                </Motion>
                <Motion
                  defaultStyle={this.state.rightBaseStyle}
                  style={springify(composedStyles.rightStyle)}
                >
                  {tweens => (
                    <div
                      style={{
                        ...composedStyles.rightStyle,
                        ...tweens,
                        flex: `1 1 ${tweens.flexBasis}%`,
                        width: `${tweens.width}%`,
                        maxWidth: `${tweens.maxWidth}%`,
                      }}
                    >
                      {rightContent}
                    </div>
                  )}
                </Motion>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    responsive: state.runtime.responsive,
  };
}

export default connect(mapStateToProps)(Modal);



// WEBPACK FOOTER //
// ./src/components/Modal/Modal.js